import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import quickDateFormat from '@dyl/utils/quickDateFormat';
import api from '@dyl/apiClients/backendApi/api';
import PropTypes from 'prop-types';

const useStyles = makeStyles(() => ({
  root: {
    width: 840,
    maxWidth: '100%',
  },
  tableRow: {
    borderBottom: '1px solid #e0e0e0',
    marginBottom: 40,
  },
  tableContainer: {
    width: '100%',
    border: 'none',
  },
  accordionDetails: {
    padding: 0,
    margin: 0,
    zIndex: -1,
  },
  accordionSummary: {
    backgroundColor: '#f7f7f7',
    flexDirection: 'row-reverse',
    marginTop: '32px',
  },
  tableCell: {
    padding: '4px 8px',
    height: '50px',
  },
  tableCellNoEllipsis: {
    padding: '4px 8px',
  },
  accordion: {
    zIndex: 0,
  },
}));

const columns = [
  { name: 'Date & Time', width: '30%' },
  { name: 'Action', width: '30%' },
  { name: 'Changed by', width: '40%' },
];
const rowsPerPage = 25;
function normaliseToArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.records)) return data.records;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.records)) return data.data.records;
  return [];
}
export default function ARCActivityLog({ logId, reload }) {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  useEffect(() => {
    if (!isExpanded || !logId) return;
    const controller = new AbortController();
    let isActive = true;
    async function fetchLogs() {
      setLoading(true);
      try {
        const { data } = await api.get(`activity_logs/arc_priming/${logId}`, {
          signal: controller.signal,
        });
        if (!isActive) return;
        const list = normaliseToArray(data);
        const onlyThisTask = list.filter(r => r?.task_id === logId);
        setRecords(onlyThisTask);
        setHasFetchedOnce(true);
      } catch (e) {
        if (controller.signal.aborted) return;
        setRecords([]);
      } finally {
        if (isActive) setLoading(false);
      }
    }

    fetchLogs();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, [isExpanded, logId, reload]);
  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => {
      const tb = new Date(b?.created_at ?? 0).getTime();
      const ta = new Date(a?.created_at ?? 0).getTime();
      return tb - ta;
    });
  }, [records]);
  useEffect(() => {
    setPage(1);
  }, [sortedRecords.length, logId]);
  const visibleRows = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return sortedRecords.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedRecords, page]);
  const totalPages = Math.ceil(sortedRecords.length / rowsPerPage) || 1;
  return (
    <div className={classes.root}>
      <Accordion
        className={classes.accordion}
        expanded={isExpanded}
        onChange={(_, v) => setIsExpanded(v)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
          <Typography>
            {isExpanded ? 'Hide ARC Activity Log' : 'Show ARC Activity Log'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          {loading ? (
            <div style={{ textAlign: 'center', width: '100%', padding: 20 }}>
              <CircularProgress />
            </div>
          ) : !hasFetchedOnce && !loading ? (
            <></>
          ) : sortedRecords.length === 0 ? (
            <Typography sx={{ textAlign: 'center' }}>
              No Logs
            </Typography>
          ) : (
            <>
              <Grid container>
                <Grid item className={classes.tableContainer}>
                  <TableContainer component={Paper}>
                    <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                      <TableHead>
                        <TableRow className={classes.tableRow}>
                          {columns.map((col) => (
                            <TableCell
                              key={col.name}
                              className={classes.tableCell}
                              style={{ maxWidth: col.width, margin: 0, padding: '8px' }}
                            >
                              {col.name}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleRows.map((row) => (
                          <TableRow
                            key={row.audit_id || `${row.task_id}-${row.created_at}`}
                            className={classes.tableRow}
                          >
                            <TableCell className={classes.tableCell}>
                              {quickDateFormat(row.created_at)}
                            </TableCell>
                            <TableCell
                              className={classes.tableCellNoEllipsis}
                              style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
                            >
                              Sent to ARC Automarker
                            </TableCell>

                            <TableCell className={classes.tableCell}>
                              {row.user_name ?? 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>

              {sortedRecords.length > rowsPerPage && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                  />
                </div>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
ARCActivityLog.propTypes = {
  logId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reload: PropTypes.any,
};